import { useState } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import PageHeader from '../../components/layout/PageHeader'
import Location from '../../hooks/Location'
import { isSuperAdminTenant } from '../../utils/superAdminTenant'
import useThemes from '../../hooks/useThemes'
import usePublishedInsights from '../../hooks/usePublishedInsights'
import useAgenda from '../../hooks/useAgenda'
import usePublishedCellKeys from '../../hooks/usePublishedCellKeys'
import Modal from '../../components/common/Modal'
import ClassificationExplanation from '../../components/benchmarkDetails/ClassificationExplanation'
import ThemeInsightsSection from '../../components/intelligence/ThemeInsightsSection'
import ThemeBenchmarkSection from '../../components/intelligence/ThemeBenchmarkSection'
import ThemeTocPathsSection from '../../components/intelligence/ThemeTocPathsSection'
import useEffectBenchmark from '../../hooks/useEffectBenchmark'
import useThemeTocPaths from '../../hooks/useThemeTocPaths'
import useThemeInterviews from '../../hooks/useThemeInterviews'
import useThemeInsights from '../../hooks/useThemeInsights'
import insightForCell from '../../helpers/intelligence/insightForCell'
import { saveInsightDraft } from '../../helpers/intelligence/intelligenceWrites'
import ThemeHeader from '../../components/intelligence/ThemeHeader'
import { segmentToThemeId, parseThemeId, insightDetailPath } from '../../helpers/intelligence/themeRoute'
import { cellDetailPath } from '../../helpers/intelligence/cellRoute'

/**
 * Eén thema met alles wat we erover weten, in de redenéervolgorde die de
 * tabbladen hadden: kop → benchmark → TOC-paden → inzichten → publicaties.
 * Elke sectie laadt en faalt los van de andere.
 *
 * AI-CONTEXT: See docs/features/intelligence-thema-eerst.md
 */
const ThemeDetailContent = () => {
    const { themeId: segment } = useParams()
    const history = useHistory()
    const client = Location()[1]

    // Een tekst uit de URL is pas een sleutel als hij de vorm heeft; 'undefined'
    // is truthy en zou anders als thema gezocht worden.
    // AI-CONTEXT: See docs/pitfalls.md#database-url-sentinel-string-persisted
    const themeId = segmentToThemeId(segment)

    const { themes, loading, loaded, error } = useThemes()
    const theme = themeId ? themes.find((t) => t.id === themeId) : undefined
    const published = usePublishedInsights()
    // Alleen de cellen van dít effect, en meteen: de sleutel staat in de URL,
    // de themalijst (de zware aanroep) hoeft er niet op gewacht te worden.
    const sleutel = parseThemeId(themeId)
    const agenda = useAgenda(sleutel ? sleutel.msiId : '')
    const gepubliceerd = usePublishedCellKeys()
    // Deze twee hebben de naam en sectoren van het thema nodig: pas zodra de
    // lijst er is (null houdt de hook stil).
    const benchmark = useEffectBenchmark(theme || null)
    const paden = useThemeTocPaths(theme || null)
    // Deze twee hebben alleen de sleutel nodig: niet wachten op de lijst.
    const interviews = useThemeInterviews(themeId || '')
    const themeInsights = useThemeInsights(themeId || '')

    // Het openen van een inzicht: bezig, of de reden dat het niet lukte.
    const [openen, setOpenen] = useState({ bezig: false, fout: '' })
    // Uitleg bij de classificatie van één cel; de cel zelf gaat mee.
    const [uitlegCel, setUitlegCel] = useState(/** @type {object|null} */(null))


    // Het kwalitatieve inzicht (vanaf de interviews) heeft een eigen pagina.
    // Bestaat het nog niet, dan maakt de server het concept (onder dit thema; hij weigert
    // een bevinding zonder thema) en gaan we daarheen. Pas beschikbaar als de
    // inzichten van het thema geladen zijn: anders zou een klik een tweede
    // concept naast het bestaande zetten.
    // AI-CONTEXT: See docs/pitfalls.md#state-gate-defaults-open-while-loading
    const openInsight = async (voorstel) => {
        if (openen.bezig) return
        const bestaand = insightForCell(themeInsights.insights, voorstel)
        if (bestaand) {
            history.push(insightDetailPath(client, bestaand.ID))
            return
        }
        setOpenen({ bezig: true, fout: '' })
        try {
            const resultaat = await saveInsightDraft({ cellType: voorstel.cellType, params: voorstel.params })
            history.push(insightDetailPath(client, resultaat.id))
        } catch (err) {
            setOpenen({ bezig: false, fout: `Het inzicht kon niet worden geopend: ${err.message}` })
        }
    }

    if (error) {
        return (
            <div className="main"><div className="main-container">
                <p className="intelligence-state intelligence-state--error">
                    De thema&apos;s konden niet worden geladen: {error.message}
                </p>
            </div></div>
        )
    }

    if (loading) {
        return (
            <div className="main"><div className="main-container">
                <p className="intelligence-state">Thema laden…</p>
            </div></div>
        )
    }

    // Pas beweren dat het thema niet bestaat als de lijst er is.
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    if (!theme) {
        return (
            <div className="main"><div className="main-container">
                <p className="intelligence-state">
                    {loaded ? 'Dit thema bestaat niet (meer).' : 'Thema laden…'}
                </p>
            </div></div>
        )
    }

    return (
        <div className="main">
            <div className="main-container">
                <PageHeader
                    heading={'Thema'}
                    AI={false}
                />

                <ThemeHeader theme={theme} />

                <ThemeBenchmarkSection benchmark={benchmark} />

                <ThemeTocPathsSection theme={theme} paths={paden} />

                <ThemeInsightsSection
                    theme={theme}
                    agenda={agenda}
                    gepubliceerd={gepubliceerd}
                    interviews={interviews}
                    published={published}
                    onOpenPublication={(insightId) => history.push(insightDetailPath(client, insightId))}
                    // De hele stand van de benchmark, niet een uitgeklede
                    // boolean: de sectie moet 'te zwak' van 'nog niet bekend'
                    // kunnen onderscheiden, want alleen de eerste is een
                    // oordeel. Eén helper leest hem (effectBenchmarkGate).
                    benchmark={benchmark}
                    onOpenCell={(row) => history.push(cellDetailPath(client, theme.id, row.cellId))}
                    onOpenInsight={themeInsights.loaded ? openInsight : null}
                    onClassificationClick={setUitlegCel}
                />

                {themeInsights.error && (
                    <p className="intelligence-state intelligence-state--error">
                        De inzichten van dit thema konden niet worden geladen: {themeInsights.error.message}
                    </p>
                )}
                {openen.bezig && <p className="intelligence-state">Inzicht openen…</p>}
                {openen.fout && <p className="intelligence-state intelligence-state--error">{openen.fout}</p>}

                {/* Sluiten is de enige handeling: er valt niets op te slaan. */}
                <Modal
                    modalOpen={uitlegCel !== null}
                    setModalOpen={() => setUitlegCel(null)}
                    confirm={() => setUitlegCel(null)}
                    pageHeader='Classificatie inzicht'
                    hideConfirm
                    cancelLabel='Sluiten'
                    content={uitlegCel && (
                        <ClassificationExplanation
                            classification={uitlegCel.classification}
                            B={uitlegCel.B}
                            p2Level={uitlegCel.gates?.p2?.level ?? 0}
                            p2Passed={uitlegCel.gates?.p2?.passed}
                            p3Status={uitlegCel.gates?.p3}
                            contrast={uitlegCel.contrast}
                        />
                    )}
                />
            </div>
        </div>
    )
}

/**
 * De poort staat om de inhoud heen en niet erin: zo draait geen enkele hook
 * voor iemand die er niet hoort te zijn. Weergavekeuze — de échte poort zit
 * server-side op de Cloud Functions.
 * AI-CONTEXT: See docs/pitfalls.md#security-cf-admin-tenant-bypass
 */
const ThemeDetail = () => {
    const client = Location()[1]

    if (!isSuperAdminTenant(client)) {
        return (
            <div className="main">
                <div className="main-container">
                    <p className="intelligence-state">
                        Deze pagina is alleen beschikbaar voor het Deccos-beheerteam.
                    </p>
                </div>
            </div>
        )
    }

    return <ThemeDetailContent />
}

export default ThemeDetail
